<script lang="ts">
  import type { Snippet } from "svelte"
  import type { HelpDialogConfig, HelpDialogContext } from "../../../../core/help/types"
  import SectionTitle from "../../../typography/_modules/section-title/index"

  type Props = {
    title: string
    count?: number | string | null
    description?: string | null
    darkMode?: boolean
    brightBackground?: boolean
    class?: string
    style?: string
    showPreconfiguration?: boolean | null
    showFilters?: boolean | null
    showMetrics?: boolean | null
    showFooter?: boolean | null
    helpModalTextId?: string
    helpModalContext?: HelpDialogContext
    helpModalFallback?: HelpDialogConfig | null
    helpModalTooltip?: string
    titleActions?: Snippet | null
    preconfiguration?: Snippet | null
    filters?: Snippet | null
    metrics?: Snippet | null
    footer?: Snippet | null
    children?: Snippet | null
  }

  let {
    title,
    count = null,
    description = null,
    darkMode = true,
    brightBackground = false,
    class: className = "",
    style: styleAttr = "",
    showPreconfiguration = null,
    showFilters = null,
    showMetrics = null,
    showFooter = null,
    helpModalTextId = "",
    helpModalContext = undefined,
    helpModalFallback = null,
    helpModalTooltip = "",
    titleActions = null,
    preconfiguration = null,
    filters = null,
    metrics = null,
    footer = null,
    children = null,
  }: Props = $props()

  const titleText = $derived(count === null || count === undefined ? title : `${title} (${count})`)
  const themeClass = $derived(darkMode && !brightBackground ? "darkMode" : "lightMode")
  const shouldRenderPreconfiguration = $derived(
    Boolean(preconfiguration) && showPreconfiguration !== false,
  )
  const shouldRenderFilters = $derived(Boolean(filters) && showFilters !== false)
  const shouldRenderMetrics = $derived(Boolean(metrics) && showMetrics !== false)
  const shouldRenderFooter = $derived(Boolean(footer) && showFooter !== false)
</script>

<section class={`table-step-layout ${themeClass} ${className}`.trim()} style={styleAttr}>
  {#if shouldRenderPreconfiguration && preconfiguration}
    <div class="table-step-preconfiguration">
      {@render preconfiguration()}
    </div>
  {/if}

  <div class="table-step-content">
    {#if titleActions}
      <SectionTitle
        title={titleText}
        small={true}
        {darkMode}
        {brightBackground}
        {helpModalTextId}
        {helpModalContext}
        {helpModalFallback}
        {helpModalTooltip}
        styleOuter="margin-bottom: 0;"
      >
        {#snippet buttonSnippet()}
          {@render titleActions()}
        {/snippet}
      </SectionTitle>
    {:else}
      <SectionTitle
        title={titleText}
        small={true}
        {darkMode}
        {brightBackground}
        {helpModalTextId}
        {helpModalContext}
        {helpModalFallback}
        {helpModalTooltip}
        styleOuter="margin-bottom: 0;"
      />
    {/if}

    {#if description}
      <p class="table-step-description">{description}</p>
    {/if}

    {#if shouldRenderFilters && filters}
      <div class="table-step-filters">
        {@render filters()}
      </div>
    {/if}

    {#if shouldRenderMetrics && metrics}
      <div class="table-step-metrics">
        {@render metrics()}
      </div>
    {/if}

    <div class="table-step-table">
      {#if children}
        {@render children()}
      {/if}
    </div>
  </div>

  {#if shouldRenderFooter && footer}
    <div class="table-step-footer">
      {@render footer()}
    </div>
  {/if}
</section>

<style lang="less">
  .table-step-layout {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    width: 100%;
  }

  .table-step-content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }

  .table-step-description {
    margin: -0.2rem 0 0;
    font-size: 1rem;
    line-height: 1.45;
  }

  .table-step-layout.darkMode .table-step-description {
    color: var(--text-invers-80);
  }

  .table-step-layout.lightMode .table-step-description {
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
  }

  .table-step-preconfiguration,
  .table-step-filters,
  .table-step-metrics,
  .table-step-table,
  .table-step-footer {
    width: 100%;
  }

  .table-step-footer {
    display: flex;
    justify-content: flex-end;
  }

  .table-step-filters + .table-step-table,
  .table-step-metrics + .table-step-table {
    margin-top: 0.45rem;
  }
</style>
